import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from './Header';
import InvitePlayerModal from './components/InvitePlayerModal';
import EditPlayerModal from './components/EditPlayerModal';
import { checkAuthAndRedirect, isAdmin } from './utils/auth';
import './scss/players.scss';
import './scss/playerRatings.scss';
import { FaPlus, FaPencilAlt, FaChevronRight, FaInfoCircle, FaCheckCircle, FaChevronDown, FaTrash } from 'react-icons/fa';
import { formatDate } from './utils/helpers';
import api from './services/apiService';
import { authService } from './services/authService';
import { useNotification } from './components/NotificationProvider';

function Players() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { showSuccess, showError } = useNotification();
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalPlayers, setTotalPlayers] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showParentInviteModal, setShowParentInviteModal] = useState(false);
    const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [isUserAdmin, setIsUserAdmin] = useState(false);
    const [adminCount, setAdminCount] = useState(0);
    const [filter, setFilter] = useState(searchParams.get('filter') || 'players'); // Get filter from URL or default to 'players'
    const [inviteCode, setInviteCode] = useState('');
    const [nextShirtNumber, setNextShirtNumber] = useState(1);
    const [acceptedPlayers, setAcceptedPlayers] = useState([]);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isSendingSMS, setIsSendingSMS] = useState(false);
    const [smsSent, setSmsSent] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [parentFormData, setParentFormData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        child_id: '',
        relationship: '',
        canRespondToMatches: true
    });
    const [isSubmittingParentForm, setIsSubmittingParentForm] = useState(false);
    const [newPlayerFormData, setNewPlayerFormData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        position: 'GK',
        preferredFoot: 'Right',
        pace: 50,
        shooting: 50,
        passing: 50,
        dribbling: 50,
        defending: 50,
        physical: 50,
        // Display values for the sliders (0-10)
        displayPace: 5,
        displayShooting: 5,
        displayPassing: 5,
        displayDribbling: 5,
        displayDefending: 5,
        displayPhysical: 5,
        shirtNumber: ''
    });
    const [isSubmittingPlayerForm, setIsSubmittingPlayerForm] = useState(false);
    const [availableShirtNumbers, setAvailableShirtNumbers] = useState([]);
    const [shirtNumberError, setShirtNumberError] = useState('');
    const [ratingsError, setRatingsError] = useState('');
    const [isAnimating, setIsAnimating] = useState(false);
    const [currentStage, setCurrentStage] = useState(1);
    const [totalStages] = useState(3);
    const [fieldErrors, setFieldErrors] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        shirtNumber: ''
    });
    // Add playerAdded state
    const [playerAdded, setPlayerAdded] = useState(null);
    const [pendingParents, setPendingParents] = useState([]);
    const [pendingParentsCount, setPendingParentsCount] = useState(0);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectingRelationshipId, setRejectingRelationshipId] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [existingPlayers, setExistingPlayers] = useState([]);
    const [isSearchingPlayers, setIsSearchingPlayers] = useState(false);
    const [showExistingPlayers, setShowExistingPlayers] = useState(false);
    const [existingParents, setExistingParents] = useState([]);
    const [isSearchingParents, setIsSearchingParents] = useState(false);
    const [showExistingParents, setShowExistingParents] = useState(false);
    const [parentSearchInput, setParentSearchInput] = useState('');
    const [selectedExistingParent, setSelectedExistingParent] = useState(null);
    const [continueWithNewParent, setContinueWithNewParent] = useState(false);
    const [showDummyData, setShowDummyData] = useState(false);

    // Demo player data for linking to profile 629
    const getDummyPlayer = () => {
        return {
            player_id: '629',
            first_name: 'Demo',
            last_name: 'Player',
            email: 'demo@example.com',
            phone: '07123 456789',
            position: 'ST',
            preferred_foot: 'Right',
            shirt_number: '10',
            pace: 75,
            shooting: 80,
            passing: 65,
            dribbling: 70,
            defending: 45,
            physical: 60,
            is_admin: '0',
            is_manager: '0',
            status: 'active',
            created_at: '2025-01-15',
            isDummy: true
        };
    };

    useEffect(() => {
        const init = async () => {
            console.log('Players component init started');
            
            // First check if user is authenticated
            const userId = sessionStorage.getItem('user_id') || sessionStorage.getItem('player_id');
            if (!userId) {
                console.log('User not authenticated, stopping init');
                checkAuthAndRedirect(navigate);
                return; // Stop here if not authenticated
            }

            // Then check if user is admin or manager
            const token = authService.getAccessToken();
            const decodedToken = authService.decodeToken(token);
            const userIsAdmin = decodedToken?.is_admin === true;
            const isUserManager = authService.isManager();
            setIsUserAdmin(userIsAdmin);
            if (!userIsAdmin && !isUserManager) {
                console.log('User not admin/manager, redirecting to dashboard');
                navigate('/dashboard');
                return;
            }

            console.log('User authenticated and authorized, fetching data');
            // If both checks pass, fetch players (which will also fetch pending parents)
            fetchPlayers();
            console.log('fetchPlayers completed (including pending parents)');
        };

        init();
    }, [navigate]);

    useEffect(() => {
        // Update admin count whenever players list changes
        const count = players.filter(p => p.is_admin).length;
        setAdminCount(count);
    }, [players]);

    useEffect(() => {
        // fetchPlayers will also fetch pending parents count
        fetchPlayers();
    }, [filter]);

    // Add a new effect to calculate the next available shirt number
    useEffect(() => {
        if (filter === 'pending') {
            // Fetch accepted players to calculate next shirt number
            fetchAcceptedPlayers();
        }
    }, [filter]);

    // Add new effect to handle URL filter changes
    useEffect(() => {
        const urlFilter = searchParams.get('filter');
        if (urlFilter && (urlFilter === 'pending' || urlFilter === 'players' || urlFilter === 'removed')) {
            setFilter(urlFilter);
        }
    }, [searchParams]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && !event.target.closest('.player-dropdown')) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdown]);

    const fetchPlayers = async () => {
        try {
            const teamId = sessionStorage.getItem('selected_team_id');
            
            // Fetch current filter data
            const response = await api.get(`${process.env.REACT_APP_API_URL}/admin_get_players.php?team_id=${teamId}&filter=${filter}`);
            const data = response.data;
            
            if (data.success) {
                // Only include demo player for 'players' filter if there are no real players
                if (filter === 'players') {
                    if (data.players.length === 0) {
                        // No real players, show demo player
                        const demoPlayer = getDummyPlayer();
                        setPlayers([demoPlayer]);
                        setTotalPlayers(1);
                        setShowDummyData(true);
                    } else {
                        // Real players exist, don't show demo player
                        setPlayers(data.players);
                        setTotalPlayers(data.total_players);
                        setShowDummyData(false);
                    }
                } else {
                    setPlayers(data.players);
                    setTotalPlayers(data.total_players);
                    setShowDummyData(false);
                }
                
                // If we're already on the pending filter, use the current players length
                if (filter === 'pending') {
                    setPendingCount(data.players.length);
                } else {
                    // Otherwise, fetch pending count separately
                    const pendingResponse = await api.get(`${process.env.REACT_APP_API_URL}/admin_get_players.php?team_id=${teamId}&filter=pending`);
                    if (pendingResponse.data.success) {
                        setPendingCount(pendingResponse.data.players.length);
                    }
                }
                
                // Always fetch pending parents count regardless of current filter
                await fetchPendingParents();
            } else {
                console.error('Error:', data.message);
                //navigate('/dashboard');
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching players:', error);
            setLoading(false);
            //navigate('/dashboard');
        }
    };

    const fetchAcceptedPlayers = async () => {
        try {
            const teamId = sessionStorage.getItem('selected_team_id');
            const response = await api.get(`${process.env.REACT_APP_API_URL}/admin_get_players.php?team_id=${teamId}&filter=players`);
            const data = response.data;
            
            if (data.success) {
                setAcceptedPlayers(data.players);
                
                // Calculate next available shirt number
                const shirtNumbers = data.players
                    .map(player => parseInt(player.shirt_number))
                    .filter(num => !isNaN(num))
                    .sort((a, b) => a - b);
                
                if (shirtNumbers.length === 0) {
                    setNextShirtNumber(1);
                } else {
                    setNextShirtNumber(shirtNumbers[shirtNumbers.length - 1] + 1);
                }
            }
        } catch (error) {
            console.error('Error fetching accepted players:', error);
        }
    };

    const fetchPendingParents = async () => {
        try {
            const teamId = sessionStorage.getItem('selected_team_id');
            console.log('Fetching pending parents for team_id:', teamId);
            console.log('API URL:', `${process.env.REACT_APP_API_URL}/get_pending_parents.php?team_id=${teamId}`);
            
            // Check if we have a token
            const token = localStorage.getItem('access_token');
            console.log('Token exists:', !!token);
            
            const response = await api.get(`${process.env.REACT_APP_API_URL}/get_pending_parents.php?team_id=${teamId}`);
            console.log('Pending parents response:', response.data);
            
            if (response.data.success) {
                setPendingParents(response.data.pending_parents);
                setPendingParentsCount(response.data.count);
                console.log('Set pending parents:', response.data.pending_parents);
                console.log('Set pending parents count:', response.data.count);
            } else {
                console.log('API returned success: false, message:', response.data.message);
            }
        } catch (error) {
            console.error('Error fetching pending parents:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                config: error.config
            });
        }
    };



    const handleToggleAdmin = async (playerId, currentStatus) => {
        try {
            // If trying to remove admin status, check if this is the last admin
            if (currentStatus) {
                const adminCount = players.filter(p => p.is_admin).length;
                if (adminCount <= 1) {
                    showError('There must be at least one admin user. Please make another user an admin before removing this admin status.');
                    return;
                }
            }

            const response = await api.post(`${process.env.REACT_APP_API_URL}/toggle_admin.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    player_id: playerId,
                    is_admin: !currentStatus
                })
            });
            const data = response.data;
            
            if (data.success) {
                // Update local state immediately
                setPlayers(players.map(player => 
                    player.player_id === playerId 
                        ? {...player, is_admin: !currentStatus}
                        : player
                ));
                showSuccess(`Admin status ${!currentStatus ? 'granted' : 'removed'} successfully`);
            } else {
                showError(data.message || 'Unable to update admin status. Please ensure there is always at least one admin user.');
            }
        } catch (error) {
            console.error('Error toggling admin status:', error);
            showError('An error occurred while updating admin status. Please try again.');
        }
    };

    const handleRemovePlayer = async (playerId, firstName, lastName) => {
        try {
            // Confirm removal
            const confirmed = window.confirm(
                `Are you sure you want to remove ${firstName} ${lastName} from the team? This action cannot be undone.`
            );
            
            if (!confirmed) {
                return;
            }

            const teamId = sessionStorage.getItem('selected_team_id');
            
            const response = await api.post(`${process.env.REACT_APP_API_URL}/remove_player.php?team_id=${teamId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    player_id: playerId
                })
            });
            const data = response.data;
            
            if (data.success) {
                // Remove player from local state
                setPlayers(prevPlayers => 
                    prevPlayers.filter(player => player.player_id !== playerId)
                );
                
                // Update total players count
                setTotalPlayers(prevCount => prevCount - 1);
                
                showSuccess(`${firstName} ${lastName} has been removed from the team`);
            } else {
                showError(data.message || 'Failed to remove player');
            }
        } catch (error) {
            console.error('Error removing player:', error);
            showError('Failed to remove player');
        }
    };

    const handleRestorePlayer = async (playerId, firstName, lastName) => {
        try {
            const teamId = sessionStorage.getItem('selected_team_id');
            
            const response = await api.post(`${process.env.REACT_APP_API_URL}/restore_player.php?team_id=${teamId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    player_id: playerId
                })
            });
            
            const data = response.data;
            
            if (data.success) {
                // Remove player from local state (since they're no longer in 'removed' filter)
                setPlayers(prevPlayers => 
                    prevPlayers.filter(player => player.player_id !== playerId)
                );
                
                showSuccess(`${firstName} ${lastName} has been restored to the team`);
            } else {
                showError(data.message || 'Failed to restore player');
            }
        } catch (error) {
            console.error('Error restoring player:', error);
            showError('Failed to restore player');
        }
    };

    const handleToggleInjuredUnavailable = async (playerId, currentStatus) => {
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/toggle_injured_unavailable.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    player_id: playerId
                })
            });
            const data = response.data;
            
            if (data.success) {
                // Refetch players to get accurate timestamp from database
                await fetchPlayers();
                const statusText = currentStatus ? 'available' : 'injured/unavailable';
                showSuccess(`Player ${statusText} status updated successfully`);
            } else {
                showError(data.message || 'Unable to update injured/unavailable status');
            }
        } catch (error) {
            console.error('Error toggling injured/unavailable status:', error);
            showError('An error occurred while updating status. Please try again.');
        }
    };

    const handleDropdownToggle = (playerId) => {
        setOpenDropdown(openDropdown === playerId ? null : playerId);
    };

    const handleDropdownAction = (action, player) => {
        setOpenDropdown(null);
        
        if (action === 'edit') {
            handleEditClick(player);
        } else if (action === 'admin') {
            handleToggleAdmin(player.player_id, player.is_admin);
        } else if (action === 'injured') {
            handleToggleInjuredUnavailable(player.player_id, !!player.injured_unavailable_at);
        } else if (action === 'remove') {
            handleRemovePlayer(player.player_id, player.first_name, player.last_name);
        } else if (action === 'restore') {
            handleRestorePlayer(player.player_id, player.first_name, player.last_name);
        }
    };

    const generateInviteCode = () => {
        let sum = 10;
        const code = [];
        const teamId = sessionStorage.getItem('selected_team_id') || '1';

        // Generate the 4-digit code that sums to 10
        for (let i = 0; i < 3; i++) {
            const digit = Math.floor(Math.random() * (sum - (3 - i))) + 1;
            code.push(digit);
            sum -= digit;
        }
        code.push(sum);

        // Shuffle the code array
        for (let i = code.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [code[i], code[j]] = [code[j], code[i]];
        }

        // Format with team ID prefix
        const fullCode = `${teamId}-${code.join('')}`;
        setInviteCode(fullCode);
        console.log('Generated Invite Code:', fullCode);
    };

    const copyToClipboard = () => {
        // Remove trailing slashes from the base URL
        const baseUrl = process.env.REACT_APP_PUBLIC_URL.replace(/\/+$/, '');
        
        // Include player ID in the code parameter if available
        let codeParam = inviteCode;
        if (playerAdded && playerAdded.id) {
            codeParam = `${inviteCode}-${playerAdded.id}`;
        }
        
        const url = `${baseUrl}/register?code=${codeParam}`;
        
        navigator.clipboard.writeText(url).then(() => {
            showSuccess('Invite link copied to clipboard!');
        }).catch(err => {
            console.error('Could not copy text: ', err);
            showError('Failed to copy invite link. Please try again.');
        });
    };

    const handleOpenInviteModal = () => {
        // Don't generate invite code yet - it will be generated after step 1
        setPhoneNumber('');
        setSmsSent(false);
        setPhoneError('');
        setPlayerAdded(null); // Reset playerAdded state
        setShowInviteModal(true);
    };

    const validatePhoneNumber = (phone) => {
        // Basic UK phone number validation - allows various formats
        const phoneRegex = /^(\+44|0)?[7-9]\d{9}$/;
        return phoneRegex.test(phone.replace(/\s+/g, ''));
    };
    
    const formatPhoneNumber = (phone) => {
        // Format phone number for sending - ensure it starts with +44
        let formatted = phone.replace(/\s+/g, '');
        
        if (formatted.startsWith('0')) {
            formatted = '+44' + formatted.substring(1);
        } else if (!formatted.startsWith('+44')) {
            formatted = '+44' + formatted;
        }
        
        return formatted;
    };

    const formatPhoneForDisplay = (phone) => {
        // If phone is 000000000000 (placeholder), show "-"
        if (phone === '000000000000') {
            return '-';
        }
        
        // If phone is empty or null, show "-"
        if (!phone || phone.trim() === '') {
            return '-';
        }
        
        // Remove any whitespace
        let cleanPhone = phone.trim();
        
        // If phone already starts with 0, return as is
        if (cleanPhone.startsWith('0')) {
            return cleanPhone;
        }
        
        // Otherwise, format as UK phone number with leading 0
        return `0${cleanPhone}`;
    };
    
    const sendSMSInvite = async () => {
        // Validate phone number
        if (!phoneNumber) {
            setPhoneError('Please enter a phone number');
            return;
        }
        
        if (!validatePhoneNumber(phoneNumber)) {
            setPhoneError('Please enter a valid UK mobile number');
            return;
        }
        
        setPhoneError('');
        setIsSendingSMS(true);
        
        try {
            // Format the SMS message
            const url = `${process.env.REACT_APP_PUBLIC_URL}/register?code=${inviteCode}`;
            const teamName = sessionStorage.getItem('selected_team_name') || 'our team';
            const message = "Hi from FirstWhistle! {$coachFullName} has invited you to our platform where you can track {$childFullName}'s football journey, view match schedules and receive team updates. Login: https://firstwhistle.app with your phone and password: {$createdPassword}";
            
            // Format the phone number 
            const formattedPhone = formatPhoneNumber(phoneNumber);
            
            // Test mode - always send to the specified test number for now
            const testNumber = '07875930733';
            
            // Send the SMS using the API endpoint
            const response = await api.post(`${process.env.REACT_APP_API_URL}/send_sms.php`, {
                phone_number: testNumber, // Use test number for now
                message: message
            });
            
            // Handle the response - PHP returns true (boolean) not a JSON object with success property
            console.log('SMS API response:', response.data);
            
            if (
                response.data === true || 
                response.data === "true" || 
                response.data === 1 || 
                response.data === "1"
            ) {
                setSmsSent(true);
                console.log('SMS sent successfully');
                
                // Clear success message and reset phone field after 5 seconds
                setTimeout(() => {
                    setSmsSent(false);
                    setPhoneNumber('');
                }, 5000);
            } else {
                setPhoneError('Failed to send SMS. Please try again or share the code manually.');
                console.error('SMS sending failed', response.data);
            }
        } catch (error) {
            console.error('Error sending SMS:', error);
            setPhoneError('Error sending SMS. Please try again later.');
        } finally {
            setIsSendingSMS(false);
        }
    };

    const handleEditClick = (player) => {
        setSelectedPlayer(player);
        setShowEditModal(true);
    };

    // Function to determine color based on stat value
    const getStatColor = (value) => {
        if (value >= 7) return '#4cd137'; // Bright green for high values (7-10)
        if (value >= 4) return '#ffa502'; // Bright orange for medium values (4-6)
        return '#ff4757'; // Bright red for low values (0-3)
    };

    const handleEditSubmit = async (formData) => {
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/update_player.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const data = response.data;
            
            if (data.success) {
                setShowEditModal(false);
                setPlayers(players.map(player => 
                    player.player_id === formData.player_id 
                        ? {...player, ...formData}
                        : player
                ));
                showSuccess('Player details updated successfully');
            } else {
                showError(data.message || 'Error updating player');
            }
        } catch (error) {
            console.error('Error updating player:', error);
            showError('An error occurred while updating the player');
        }
    };

    const handleAcceptPlayer = async (playerId) => {
        try {
            const teamId = sessionStorage.getItem('selected_team_id');
            // Get the player's existing shirt number or use the next available one
            const player = players.find(p => p.player_id === playerId);
            const shirtNumber = player?.shirt_number || nextShirtNumber;

            const response = await api.post(`${process.env.REACT_APP_API_URL}/accept_player.php`, {
                player_id: playerId,
                shirt_number: shirtNumber,
                team_id: teamId
            });
            const data = response.data;
            
            if (data.success) {
                const playerName = player ? `${player.first_name} ${player.last_name}` : 'Player';
                
                // Show success notification
                showSuccess(`${playerName} has been accepted to the team and assigned shirt number ${shirtNumber}. A welcome message has been sent to their phone.`);
                
                // Refresh the player lists
                fetchPlayers();
                if (filter === 'pending') {
                    fetchAcceptedPlayers();
                }
            } else {
                showError(data.message || 'Unable to accept player. Please try again.');
            }
        } catch (error) {
            console.error('Error accepting player:', error);
            showError('An error occurred while accepting the player. Please try again.');
        }
    };

    // Update setFilter to also update URL
    const handleFilterChange = (newFilter) => {
        console.log('Filter changing from', filter, 'to', newFilter);
        setFilter(newFilter);
        setSearchParams({ filter: newFilter });
    };

    const handleApproveParent = async (relationshipId) => {
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/accept_parent.php`, {
                relationship_id: relationshipId,
                action: 'approve'
            });

            if (response.data.success) {
                showSuccess('Parent approved successfully');
                fetchPendingParents(); // Refresh the list
            } else {
                showError(response.data.message || 'Failed to approve parent');
            }
        } catch (error) {
            console.error('Error approving parent:', error);
            showError('An error occurred while approving parent');
        }
    };

    const handleRejectParent = async (relationshipId, reason = '') => {
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/accept_parent.php`, {
                relationship_id: relationshipId,
                action: 'reject',
                rejection_reason: reason
            });

            if (response.data.success) {
                showSuccess('Parent rejected successfully');
                fetchPendingParents(); // Refresh the list
                setShowRejectModal(false);
                setRejectionReason('');
                setRejectingRelationshipId(null);
            } else {
                showError(response.data.message || 'Failed to reject parent');
            }
        } catch (error) {
            console.error('Error rejecting parent:', error);
            showError('An error occurred while rejecting parent');
        }
    };

    const openRejectModal = (relationshipId) => {
        setRejectingRelationshipId(relationshipId);
        setRejectionReason('');
        setShowRejectModal(true);
    };

    const handleRejectSubmit = () => {
        if (rejectingRelationshipId) {
            handleRejectParent(rejectingRelationshipId, rejectionReason);
        }
    };

    const handleOpenParentInviteModal = (preSelectedPlayerId = null) => {
        // Make sure we have the latest list of accepted players for the dropdown
        const fetchPlayers = async () => {
            try {
                const teamId = sessionStorage.getItem('selected_team_id');
                const response = await api.get(`${process.env.REACT_APP_API_URL}/admin_get_players.php?team_id=${teamId}&filter=players`);
                const data = response.data;
                
                if (data.success) {
                    setAcceptedPlayers(data.players);
                    
                    // Check if there are any players to link
                    if (data.players.length === 0) {
                        showError('No players found. Please add a player first before inviting a parent/guardian.');
                        return;
                    }
                    
                    // Reset parent form with default values
                    setParentFormData({
                        first_name: '',
                        last_name: '',
                        phone: '',
                        email: '',
                        child_id: preSelectedPlayerId || (data.players.length > 0 ? data.players[0].player_id : ''),
                        relationship: '',
                        canRespondToMatches: true
                    });
                    
                    // Reset existing parent selection
                    setSelectedExistingParent(null);
                    setExistingParents([]);
                    setShowExistingParents(false);
                    setContinueWithNewParent(false);
                    setParentSearchInput('');
                    
                    // Show the modal after data is loaded
                    setShowParentInviteModal(true);
                } else {
                    showError('Error loading players: ' + data.message);
                }
            } catch (error) {
                console.error('Error fetching players for parent invite:', error);
                showError('Failed to load players. Please try again.');
            }
        };
        
        fetchPlayers();
    };

    const handleParentFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newFormData = {
            ...parentFormData,
            [name]: type === 'checkbox' ? checked : value
        };
        setParentFormData(newFormData);
        
        // If the player selection changes, re-run the search to update "Already Added" status
        // Pass the new player_id value directly to the search function
        if (name === 'child_id' && parentSearchInput.trim().length >= 2) {
            searchExistingParents(parentSearchInput, value);
        }
    };

    const handleParentSearchChange = (e) => {
        const value = e.target.value;
        setParentSearchInput(value);

        // Search for existing parents when input changes
        // Only search if we're not creating a new parent and haven't selected an existing one
        if (!selectedExistingParent && !continueWithNewParent) {
            if (value.trim().length >= 2) {
                // Reset search state when searching
                setShowExistingParents(false);
                setExistingParents([]);
                setSelectedExistingParent(null);
                setContinueWithNewParent(false);
                // Search with the combined input
                searchExistingParents(value);
            } else {
                setShowExistingParents(false);
                setExistingParents([]);
                setSelectedExistingParent(null);
                setContinueWithNewParent(false);
            }
        }
    };

    // Validation function for parent form
    const isParentFormValid = () => {
        // If an existing parent is selected, only need player to link
        if (selectedExistingParent) {
            return parentFormData.child_id && parentFormData.child_id.trim() !== '';
        }
        
        // For new parent, need all required fields
        return (
            parentFormData.child_id && parentFormData.child_id.trim() !== '' &&
            parentFormData.first_name && parentFormData.first_name.trim() !== '' &&
            parentFormData.last_name && parentFormData.last_name.trim() !== '' &&
            parentFormData.phone && parentFormData.phone.trim() !== ''
        );
    };

    const handleParentFormSubmit = async (e) => {
        e.preventDefault();
        
        try {
            // Input validation
            if (!parentFormData.first_name.trim()) {
                showError('First name is required');
                return;
            }
            
            if (!parentFormData.last_name.trim()) {
                showError('Last name is required');
                return;
            }
            
            if (!parentFormData.child_id) {
                showError('Please select a player to link');
                return;
            }
            
            // Set loading state
            setIsSubmittingParentForm(true);
            
            if (selectedExistingParent) {
                // Handle existing parent
                const teamId = sessionStorage.getItem('selected_team_id');
                
                const response = await api.post(`${process.env.REACT_APP_API_URL}/add_existing_parent_to_team.php`, {
                    parent_id: selectedExistingParent.parent_id,
                    team_id: teamId,
                    player_id: parentFormData.child_id,
                    allow_match_response: parentFormData.canRespondToMatches
                });
                
                if (response.data.success) {
                    showSuccess(`Parent ${selectedExistingParent.first_name} ${selectedExistingParent.last_name} successfully added to manage ${response.data.player_name}`);
                    setShowParentInviteModal(false);
                    setSelectedExistingParent(null);
                    
                    // Refresh the players list
                    fetchPlayers();
                } else {
                    showError(response.data.message || 'Failed to add parent to team');
                }
            } else {
                // Handle new parent
                if (!parentFormData.phone.trim()) {
                    showError('Mobile number is required');
                    return;
                }
                
                // Clean phone number
                const phoneNumber = parentFormData.phone.replace(/\s+/g, '');
                if (!validatePhoneNumber(phoneNumber)) {
                    showError('Please enter a valid UK mobile number');
                    return;
                }
                
                // Create a clean object for submission
                const submissionData = {
                    ...parentFormData,
                    phone: phoneNumber,
                    relationship_type: 'primary',
                    is_legal_guardian: 1,
                    can_respond_to_matches: parentFormData.canRespondToMatches ? 1 : 0,
                    auto_approve: true // Automatically approve parents invited by coaches
                };
                
                // Use "Other" as default relationship if not specified
                if (!submissionData.relationship || submissionData.relationship.trim() === '') {
                    submissionData.relationship = 'Other';
                }
                
                // Ensure email is only included if not empty
                if (!submissionData.email || submissionData.email.trim() === '') {
                    delete submissionData.email;
                }
                
                // Send API request
                const response = await api.post(`${process.env.REACT_APP_API_URL}/invite_parent.php`, submissionData);
                
                const data = response.data;
                
                if (data.success) {
                    // Create a more detailed success message if SMS was sent
                    let successMessage = data.message;
                    
                    if (data.is_new_parent && data.sms_sent) {
                        // Find the child name from the acceptedPlayers array
                        const child = acceptedPlayers.find(player => player.player_id === parentFormData.child_id);
                        const childName = child ? `${child.first_name} ${child.last_name}` : 'the player';
                        
                        successMessage = `Parent invited successfully! An SMS with login details has been sent to ${parentFormData.first_name} ${parentFormData.last_name} for managing ${childName}.`;
                    }
                    
                    showSuccess(successMessage);
                    setShowParentInviteModal(false);
                    
                    // Refresh player data to show updated parent info
                    fetchPlayers();
                } else {
                    showError(data.message || 'Failed to invite parent');
                }
            }
        } catch (error) {
            console.error('Error inviting parent:', error);
            showError('An error occurred while inviting the parent. Please try again.');
        } finally {
            setIsSubmittingParentForm(false);
        }
    };

    // Check if this is an academy team
    const isAcademyTeam = () => {
        const selectedTeamType = sessionStorage.getItem('selected_team_type');
        return selectedTeamType === 'academy';
    };

    // Check if players are 13 or older based on the team age group
    const isAgedThirteenOrAbove = () => {
        const ageGroup = sessionStorage.getItem('selected_team_age_group');
        if (!ageGroup) return false;
        
        // Extract the age number from the age group string (e.g., "U13s" -> 13)
        const ageMatch = ageGroup.match(/U(\d+)s?/i);
        if (!ageMatch || ageMatch.length < 2) return false;
        
        const age = parseInt(ageMatch[1], 10);
        return age >= 13;
    };

    // Check if this is an academy team with U13s or younger (should hide contact fields)
    const isAcademyU13OrYounger = () => {
        const selectedTeamType = sessionStorage.getItem('selected_team_type');
        const ageGroup = sessionStorage.getItem('selected_team_age_group');
        
        if (selectedTeamType !== 'academy') return false;
        if (!ageGroup) return false;
        
        // Extract the age number from the age group string (e.g., "U13s" -> 13)
        const ageMatch = ageGroup.match(/U(\d+)s?/i);
        if (!ageMatch || ageMatch.length < 2) return false;
        
        const age = parseInt(ageMatch[1], 10);
        return age <= 13;
    };

    const fetchAvailableShirtNumbers = async (teamId) => {
        try {
            const response = await api.get(
                `${process.env.REACT_APP_API_URL}/register/get_available_shirt_numbers.php?team_id=${teamId}`
            );

            if (response.data.success) {
                setAvailableShirtNumbers(response.data.available_numbers || []);
                if (response.data.suggested_number) {
                    setNewPlayerFormData(prev => ({ 
                        ...prev, 
                        shirtNumber: response.data.suggested_number.toString() 
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching available shirt numbers:', error);
        }
    };

    // Calculate positions for hexagon points for the player ratings chart
    const calculatePoint = (value, index, total = 6) => {
        const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
        const radius = (value / 100) * 50; // 50 is the max radius
        return {
            x: 50 + radius * Math.cos(angle),
            y: 50 + radius * Math.sin(angle)
        };
    };

    const handleNewPlayerFormChange = (e) => {
        const { name, value } = e.target;
        
        setNewPlayerFormData({
            ...newPlayerFormData,
            [name]: value
        });

        // Clear field error when user types
        if (fieldErrors[name]) {
            setFieldErrors({
                ...fieldErrors,
                [name]: ''
            });
        }

        // Handle shirt number change
        if (name === 'shirtNumber') {
            validateShirtNumber(value);
        }
        
        // Search for existing players when last name is typed
        if (name === 'last_name' && value.length >= 2) {
            searchExistingPlayers(newPlayerFormData.first_name, value);
        } else if (name === 'first_name' && newPlayerFormData.last_name.length >= 2) {
            searchExistingPlayers(value, newPlayerFormData.last_name);
        } else if (value.length < 2) {
            setShowExistingPlayers(false);
            setExistingPlayers([]);
        }
    };

    const validateShirtNumber = (number) => {
        if (!number) {
            setShirtNumberError('Please select a shirt number');
            return false;
        }

        // Check if the number is in the available numbers list
        if (!availableShirtNumbers.includes(parseInt(number))) {
            setShirtNumberError('This shirt number is already taken');
            return false;
        }

        setShirtNumberError('');
        return true;
    };
    
    const handleRatingChange = (ability, displayValue) => {
        // Convert display value (0-10) to stored value (0-100)
        const storedValue = parseInt(displayValue) * 10;
        
        setNewPlayerFormData(prevData => {
            // Update the slider's progress indicator if we're on the client side
            if (typeof document !== 'undefined') {
                const slider = document.getElementById(`${ability}-slider-add`);
                if (slider) {
                    const percent = (displayValue / 10) * 100;
                    slider.style.setProperty('--value-percent', `${percent}%`);
                }
            }
            
            return { 
                ...prevData, 
                [ability]: storedValue,
                [`display${ability.charAt(0).toUpperCase() + ability.slice(1)}`]: parseInt(displayValue)
            };
        });
        
        // Clear ratings error when user changes a rating
        setRatingsError('');
    };
    
    const handleFootPreference = (foot) => {
        setNewPlayerFormData({ ...newPlayerFormData, preferredFoot: foot });
    };

    const searchExistingPlayers = async (firstName, lastName) => {
        if ((!firstName || firstName.length < 2) && (!lastName || lastName.length < 2)) {
            setShowExistingPlayers(false);
            setExistingPlayers([]);
            return;
        }

        setIsSearchingPlayers(true);
        try {
            const params = new URLSearchParams();
            if (firstName) params.append('first_name', firstName);
            if (lastName) params.append('last_name', lastName);
            
            const response = await api.get(`${process.env.REACT_APP_API_URL}/search_existing_players.php?${params}`);
            
            if (response.data.success) {
                setExistingPlayers(response.data.players);
                setShowExistingPlayers(response.data.players.length > 0);
            } else {
                setExistingPlayers([]);
                setShowExistingPlayers(false);
            }
        } catch (error) {
            console.error('Error searching existing players:', error);
            setExistingPlayers([]);
            setShowExistingPlayers(false);
        } finally {
            setIsSearchingPlayers(false);
        }
    };

    // Parse search input to determine if it's a phone number or name
    const parseSearchInput = (input) => {
        if (!input || input.trim().length === 0) {
            return { phone: '', firstName: '', lastName: '' };
        }
        
        const trimmed = input.trim();
        
        // Check if input looks like a phone number (contains mostly digits)
        const digitsOnly = trimmed.replace(/\D/g, '');
        const isPhoneNumber = digitsOnly.length >= 10 && digitsOnly.length <= 15;
        
        if (isPhoneNumber) {
            return { phone: trimmed, firstName: '', lastName: '' };
        }
        
        // Otherwise, treat as name - split by spaces
        const nameParts = trimmed.split(/\s+/).filter(part => part.length > 0);
        
        if (nameParts.length === 0) {
            return { phone: '', firstName: '', lastName: '' };
        } else if (nameParts.length === 1) {
            // Single word - could be first or last name, search both fields but only use firstName
            // Set lastName to empty so API treats it as single term search
            return { phone: '', firstName: nameParts[0], lastName: '' };
        } else {
            // Multiple words - first is first name, last is last name
            const firstName = nameParts[0];
            const lastName = nameParts.slice(1).join(' '); // Join remaining parts as last name
            return { phone: '', firstName, lastName };
        }
    };

    const searchExistingParents = async (searchInput, playerId = null) => {
        if (!searchInput || searchInput.trim().length < 2) {
            setShowExistingParents(false);
            setExistingParents([]);
            return;
        }

        const { phone, firstName, lastName } = parseSearchInput(searchInput);
        
        // Only search if we have at least one search parameter with sufficient length
        const hasPhone = phone && phone.replace(/\D/g, '').length >= 10;
        const hasFirstName = firstName && firstName.trim().length >= 2;
        const hasLastName = lastName && lastName.trim().length >= 2;
        
        if (!hasPhone && !hasFirstName && !hasLastName) {
            setShowExistingParents(false);
            setExistingParents([]);
            return;
        }

        setIsSearchingParents(true);
        try {
            const teamId = sessionStorage.getItem('selected_team_id');
            const searchParams = {
                team_id: teamId
            };
            
            // Include the selected player_id if provided, so we can check if parent is already linked to THIS specific child
            // Use the provided playerId parameter, or fall back to parentFormData.child_id
            const targetPlayerId = playerId || parentFormData.child_id;
            if (targetPlayerId) {
                searchParams.player_id = targetPlayerId;
            }
            
            // Only include search parameters that have values
            if (hasPhone) {
                searchParams.phone = phone;
            }
            if (hasFirstName) {
                searchParams.first_name = firstName.trim();
            }
            if (hasLastName) {
                searchParams.last_name = lastName.trim();
            }
            
            const response = await api.post(`${process.env.REACT_APP_API_URL}/search_existing_parents.php`, searchParams);
            
            if (response.data.success) {
                // The API now returns is_already_added status directly
                const parentsWithStatus = response.data.parents.map(parent => ({
                    ...parent,
                    isAlreadyAdded: parent.is_already_added === "1" || parent.is_already_added === 1
                }));
                
                setExistingParents(parentsWithStatus);
                setShowExistingParents(true); // Always show results section, even if empty
            } else {
                setExistingParents([]);
                setShowExistingParents(true); // Show results section even on error
            }
        } catch (error) {
            console.error('Error searching existing parents:', error);
            setExistingParents([]);
            setShowExistingParents(true); // Show results section even on error
        } finally {
            setIsSearchingParents(false);
        }
    };

    const handleSelectExistingPlayer = async (player) => {
        try {
            const teamId = sessionStorage.getItem('selected_team_id');
            
            // Add existing player to the current team
            const response = await api.post(`${process.env.REACT_APP_API_URL}/add_existing_player_to_team.php`, {
                player_id: player.player_id,
                team_id: teamId
            });
            
            if (response.data.success) {
                // Hide the existing players list
                setShowExistingPlayers(false);
                setExistingPlayers([]);
                
                // Set the player added info for stage 2
                setPlayerAdded({
                    id: player.player_id,
                    name: `${player.first_name} ${player.last_name}`,
                    isExistingPlayer: true
                });
                
                // Move to stage 2
                setCurrentStage(2);
                
                // Refresh the players list
                fetchPlayers();
            } else {
                showError(response.data.message || 'Failed to add player to team');
            }
        } catch (error) {
            console.error('Error adding existing player to team:', error);
            showError('Error adding player to team. Please try again.');
        }
    };

    const handleSelectExistingParent = (parent) => {
        // Set the selected parent and populate the form
        setSelectedExistingParent(parent);
        setParentFormData({
            ...parentFormData,
            first_name: parent.first_name,
            last_name: parent.last_name,
            phone: parent.display_phone === 'Not provided' ? '' : parent.display_phone,
            email: parent.email || ''
        });
        
        // Hide the existing parents list
        setShowExistingParents(false);
        setExistingParents([]);
        setContinueWithNewParent(false);
    };

    const handleOpenAddPlayerModal = () => {
        setShowAddPlayerModal(true);
        setIsAnimating(false);
        setCurrentStage(1);
        
        // Reset form data
        setNewPlayerFormData({
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            position: 'GK',
            preferredFoot: 'Right',
            pace: 50,
            shooting: 50,
            passing: 50,
            dribbling: 50,
            defending: 50,
            physical: 50,
            displayPace: 5,
            displayShooting: 5,
            displayPassing: 5,
            displayDribbling: 5,
            displayDefending: 5,
            displayPhysical: 5,
            shirtNumber: ''
        });
        
        // Clear errors and existing players
        setShirtNumberError('');
        setRatingsError('');
        setExistingPlayers([]);
        setShowExistingPlayers(false);
        
        // Fetch available shirt numbers
        const teamId = sessionStorage.getItem('selected_team_id');
        fetchAvailableShirtNumbers(teamId);
        
        // Add small delay before starting animation
        setTimeout(() => {
            setIsAnimating(true);
        }, 300);
    };

    const handleNextStage = () => {
        // Reset field errors
        const errors = {
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            shirtNumber: ''
        };
        let isValid = true;
        
        if (currentStage === 1) {
            // Validate first name
            if (!newPlayerFormData.first_name.trim()) {
                errors.first_name = 'First name is required';
                isValid = false;
            }
            
            // Validate last name
            if (!newPlayerFormData.last_name.trim()) {
                errors.last_name = 'Last name is required';
                isValid = false;
            }
            
            // Only validate phone and email if not an academy team
            if (!isAcademyTeam()) {
                // Validate phone
                if (!newPlayerFormData.phone.trim()) {
                    errors.phone = 'Mobile number is required';
                    isValid = false;
                } else {
                    // Clean phone number
                    const phoneNumber = newPlayerFormData.phone.replace(/\s+/g, '');
                    if (!validatePhoneNumber(phoneNumber)) {
                        errors.phone = 'Please enter a valid UK mobile number';
                        isValid = false;
                    }
                }
                
                // Validate email if provided
                if (newPlayerFormData.email.trim() && !validateEmail(newPlayerFormData.email)) {
                    errors.email = 'Please enter a valid email address';
                    isValid = false;
                }
            }
        } else if (currentStage === 2) {
            // Validate shirt number
            if (!newPlayerFormData.shirtNumber) {
                errors.shirtNumber = 'Please select a shirt number';
                isValid = false;
            } else if (!availableShirtNumbers.includes(parseInt(newPlayerFormData.shirtNumber))) {
                errors.shirtNumber = 'This shirt number is already taken';
                isValid = false;
            }
        }
        
        // Update field errors state
        setFieldErrors(errors);
        
        if (isValid && currentStage < totalStages) {
            setCurrentStage(currentStage + 1);
            
            // If moving to player abilities stage, reset animation
            if (currentStage === 2) {
                setIsAnimating(false);
                setTimeout(() => {
                    setIsAnimating(true);
                }, 300);
            }
        }
    };

    const handlePreviousStage = () => {
        if (currentStage > 1) {
            setCurrentStage(currentStage - 1);
        }
    };

    const handleAddPlayerSubmit = async (e) => {
        e.preventDefault();
        
        try {
            // Input validation
            if (!newPlayerFormData.first_name.trim()) {
                showError('First name is required');
                return;
            }
            
            if (!newPlayerFormData.last_name.trim()) {
                showError('Last name is required');
                return;
            }
            
            // Only validate phone and email if not an academy team
            if (!isAcademyTeam()) {
                if (!newPlayerFormData.phone.trim()) {
                    showError('Mobile number is required');
                    return;
                }
                
                // Clean phone number
                const phoneNumber = newPlayerFormData.phone.replace(/\s+/g, '');
                if (!validatePhoneNumber(phoneNumber)) {
                    showError('Please enter a valid UK mobile number');
                    return;
                }
            }
            
            // Validate shirt number
            if (!validateShirtNumber(newPlayerFormData.shirtNumber)) {
                return;
            }
            
            // Check if all ability ratings are the same
            const abilityValues = [
                newPlayerFormData.pace, 
                newPlayerFormData.shooting, 
                newPlayerFormData.passing, 
                newPlayerFormData.dribbling, 
                newPlayerFormData.defending, 
                newPlayerFormData.physical
            ];
            
            const allSameValue = abilityValues.every(val => val === abilityValues[0]);
            const allDefaultValue = abilityValues.every(val => val === 50);
            
            if (allSameValue || allDefaultValue) {
                // Display an error message
                setRatingsError('Please adjust player ratings. They cannot all be the same value.');
                // Scroll to the ratings section
                const ratingsSection = document.querySelector('.player-ratings-container');
                if (ratingsSection) {
                    ratingsSection.scrollIntoView({ behavior: 'smooth' });
                }
                return;
            }
            
            // Set loading state
            setIsSubmittingPlayerForm(true);
            
            // Get team ID
            const teamId = sessionStorage.getItem('selected_team_id');
            
            // Remove display values before submitting
            const { 
                displayPace, displayShooting, displayPassing, 
                displayDribbling, displayDefending, displayPhysical, 
                ...submitData 
            } = newPlayerFormData;
            
            // Create a clean submission object
            const submissionData = {
                ...submitData,
                team_id: teamId
            };
            
            // Add phone if not an academy U13 or younger team and phone is provided
            if (!isAcademyU13OrYounger() && newPlayerFormData.phone) {
                submissionData.phone = newPlayerFormData.phone.replace(/\s+/g, '');
            }
            
            // Ensure email is only included if not empty and not an academy U13 or younger team
            if (!isAcademyU13OrYounger() && newPlayerFormData.email && newPlayerFormData.email.trim() !== '') {
                submissionData.email = newPlayerFormData.email.trim();
            }
            
            // Send API request
            const response = await api.post(`${process.env.REACT_APP_API_URL}/add_player.php`, submissionData);
            
            const data = response.data;
            
            if (data.success) {
                showSuccess('Player added successfully');
                setShowAddPlayerModal(false);
                
                // Refresh player data
                fetchPlayers();
            } else {
                showError(data.message || 'Failed to add player');
            }
        } catch (error) {
            console.error('Error adding player:', error);
            showError('An error occurred while adding the player. Please try again.');
        } finally {
            setIsSubmittingPlayerForm(false);
        }
    };

    // Add a function to validate email format
    const validateEmail = (email) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    // Inside the Players component, add a new function to handle the invite form submission
    const handleInviteSubmit = async (formData) => {
        try {
            const teamId = sessionStorage.getItem('selected_team_id');
            
            // Generate the invite code first, before we need to use it
            let sum = 10;
            const code = [];
            
            // Generate the 4-digit code that sums to 10
            for (let i = 0; i < 3; i++) {
                const digit = Math.floor(Math.random() * (sum - (3 - i))) + 1;
                code.push(digit);
                sum -= digit;
            }
            code.push(sum);
            
            // Shuffle the code array
            for (let i = code.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [code[i], code[j]] = [code[j], code[i]];
            }
            
            // Format with team ID prefix
            const generatedInviteCode = `${teamId}-${code.join('')}`;
            
            // Add team_id to the form data
            const submitData = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    team_id: teamId
                })
            };
            
            // Submit the data to the API
            const response = await api.post(`${process.env.REACT_APP_API_URL}/invite_player.php`, submitData);
            
            if (response.data.success) {
                // Set player added info
                setPlayerAdded({
                    id: response.data.player_id,
                    name: response.data.player_name || `${formData.first_name} ${formData.last_name}`
                });
                
                // Set the invite code in the state
                setInviteCode(generatedInviteCode);
                
                // Automatically send SMS invitation to the player's phone
                try {
                    // Format the SMS message
                    const baseUrl = process.env.REACT_APP_PUBLIC_URL.replace(/\/+$/, '');
                    let codeParam = generatedInviteCode;
                    
                    // Include player ID in the code parameter if available
                    if (response.data.player_id) {
                        codeParam = `${generatedInviteCode}-${response.data.player_id}`;
                    }
                    
                    const url = `${baseUrl}/register?code=${codeParam}`;
                    const teamName = sessionStorage.getItem('selected_team_name') || 'our team';
                    const message = `You've been added to ${teamName} on FirstWhistle. Complete your registration here: ${url}`;
                    
                    // Format the phone number
                    const formattedPhone = formatPhoneNumber(formData.phone);
                    
                    // Send the SMS using the API endpoint
                    const smsResponse = await api.post(`${process.env.REACT_APP_API_URL}/send_sms.php`, {
                        phone_number: formattedPhone,
                        message: message
                    });
                    
                    console.log('SMS invitation sent automatically:', smsResponse.data);
                } catch (smsError) {
                    console.error('Error sending automatic SMS invitation:', smsError);
                    // We don't show an error to the user here since the player was added successfully
                    // The admin can still copy the invite link if needed
                }
            } else {
                showError(response.data.message || 'Failed to add player');
            }
        } catch (error) {
            console.error('Error adding player:', error);
            showError('An error occurred while adding the player. Please try again.');
        }
    };

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    const positions = [
        { value: 'GK', label: 'Goalkeeper' },
        { value: 'CB', label: 'Center Back' },
        { value: 'LB', label: 'Left Back' },
        { value: 'RB', label: 'Right Back' },
        { value: 'CDM', label: 'Defending Midfielder' },
        { value: 'CM', label: 'Central Midfielder' },
        { value: 'CAM', label: 'Attacking Midfielder' },
        { value: 'LW', label: 'Left Winger' },
        { value: 'RW', label: 'Right Winger' },
        { value: 'ST', label: 'Striker' }
    ];
    
    const footOptions = [
        'Left',
        'Right',
        'Both'
    ];

    return (
        <div className="players-page container">
            <Header />
            <button className="back-button" onClick={() => window.history.back()}>← Back</button>
            <h1>Manage Players</h1>
            <div className="players-summary">
                <div className="summary-card">
                    <h3>Total Players</h3>
                    <span className="count">{totalPlayers}</span>
                </div>
                {/* <div className="summary-card">
                    <h3>Admins</h3>
                    <span className="count">{adminCount}</span>
                </div> */}
                {(() => {
                    // For non-academy teams, there's only 1 button
                    const singleButton = !isAcademyTeam();
                    
                    return (
                        <>
                            {!isAcademyTeam() && (
                                <button 
                                    className={`invite-btn ${singleButton ? 'single-button' : ''}`}
                                    onClick={handleOpenInviteModal}
                                >
                                    <FaPlus size={24} />
                                    <span>Add Player</span>
                                </button>
                            )}
                            {isAcademyTeam() && (
                                <button 
                                    className="invite-btn add-player-btn"
                                    onClick={handleOpenAddPlayerModal}
                                >
                                    <FaPlus size={24} />
                                    <span>Add Player</span>
                                </button>
                            )}
                            {isAcademyTeam() && (
                                <button 
                                    className="invite-btn invite-parent-btn"
                                    onClick={() => handleOpenParentInviteModal()}
                                >
                                    <FaPlus size={24} />
                                    <span>Invite Parent / Guardian</span>
                                </button>
                            )}
                        </>
                    );
                })()}
            </div>

            <div className="filter-tabs">
                <button 
                    className={`filter-tab ${filter === 'players' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('players')}
                >
                    Accepted
                </button>
                <button 
                    className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('pending')}
                >
                    Pending {(pendingParentsCount + pendingCount) > 0 && (
                        <span className="pending-count">{pendingParentsCount + pendingCount}</span>
                    )}
                </button>
                <button 
                    className={`filter-tab ${filter === 'removed' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('removed')}
                >
                    Removed
                </button>
            </div>
            
            {showDummyData && (
                <div className="dummy-data-notice global-notice">
                    <h4>Welcome to FirstWhistle!</h4>
                    <p>We're showing a demo player to help you experience the app.
                    Your team's players will appear here once they've been added by your coach.</p>
                </div>
            )}

            {/* Pending: parents first, then players */}
            {filter === 'pending' && (
                <section className="pending-section pending-section--parents">
                    <h2 className="pending-section-title">Parents awaiting approval</h2>
                    {pendingParents.length > 0 ? (
                        <div className="players-list">
                            {pendingParents.map(parent => (
                                <div key={parent.relationship_id} className="player-card">
                                    <div className="player-info">
                                        <div className="player-name">
                                            {parent.parent.first_name} {parent.parent.last_name}
                                        </div>
                                        <div className="player-details">
                                            <div className="player-basic-info">
                                                <div className="detail-item">
                                                    <span className="label">Child:</span>
                                                    <span className="value">{parent.child.first_name} {parent.child.last_name} (#{parent.child.shirt_number})</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="label">Relationship:</span>
                                                    <span className="value">{parent.relationship}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="label">Phone:</span>
                                                    <span className="value">{formatPhoneForDisplay(parent.parent.phone)}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="label">Email:</span>
                                                    <span className="value">{parent.parent.email}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="label">Registration Date:</span>
                                                    <span className="value">{formatDate(parent.registration_date)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="player-card-actions">
                                        <button
                                            className="approve-btn"
                                            onClick={() => handleApproveParent(parent.relationship_id)}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            className="reject-btn"
                                            onClick={() => openRejectModal(parent.relationship_id)}
                                        >
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="pending-section-empty">
                            {isAcademyTeam() ? (
                                <>
                                    No parents awaiting approval -{' '}
                                    <button
                                        type="button"
                                        className="pending-section-invite-link"
                                        onClick={() => handleOpenParentInviteModal()}
                                    >
                                        Invite Parent
                                    </button>
                                </>
                            ) : (
                                'No parents awaiting approval.'
                            )}
                        </p>
                    )}
                </section>
            )}

            {filter === 'pending' && (
                <h2 className="pending-section-title pending-section-title--players">Players awaiting approval</h2>
            )}
            
            {/* Players List - Show for 'players', 'pending', and 'removed' filters */}
            {(filter === 'players' || filter === 'removed' || filter === 'pending') && (
                <div className="players-list">
                    {players.length > 0 ? (
                        players.map(player => (
                            <div 
                                key={player.player_id} 
                                className="player-card"
                                onClick={() => navigate(`/profile/${player.player_id}`)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="player-info">
                                    <div className="player-name">
                                        {/* Conditional rendering of buttons based on filter */}
                                        {filter === 'players' && (
                                            <span className="player-number">{player.shirt_number || '-'}.</span>
                                        )}
                                        {player.first_name} {player.last_name}
                                    </div>
                                    <div className="player-details">
                                        <div className="player-basic-info">
                                            <div className="detail-item">
                                                <span className="label">Mobile:</span>
                                                <span className="value">{formatPhoneForDisplay(player.phone)}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">Joined:</span>
                                                <span className="value">{formatDate(player.created_at)}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">Last Played:</span>
                                                <span className="value">
                                                    { player.last_played ? formatDate(player.last_played) : '-'}
                                                </span>
                                            </div>
                                            {player.injured_unavailable_at && (
                                                <div className="detail-item">
                                                    <span className="label">Status:</span>
                                                    <span className="value" style={{ color: '#ff6b6b', fontWeight: 'bold' }}>
                                                        Injured / Unavailable
                                                        {player.injured_unavailable_at && (
                                                            <span style={{ fontSize: '0.85em', fontWeight: 'normal', marginLeft: '8px', color: '#ccc' }}>
                                                                (since {formatDate(player.injured_unavailable_at)})
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="detail-item">
                                                <span className="label">Position:</span>
                                                <span className="value">{player.position || '-'}</span>
                                            </div>
                                                                                         {/* Permission checkmark for academy teams (U14+ only) */}
                                             {isAcademyTeam() && !isAcademyU13OrYounger() && (
                                                 <div className="detail-item">
                                                     <span className="label">Access to app:</span>
                                                     <span className="value">
                                                         {player.has_parent_permission == 1 ? (
                                                             <span className="permission-granted">
                                                                 <FaCheckCircle className="permission-icon" />
                                                                 Granted
                                                                 {player.parent_name && (
                                                                     <span className="parent-name">
                                                                         by {player.parent_name}
                                                                         {player.parent_relationship && (
                                                                             <span className="relationship-info">
                                                                                 ({player.parent_relationship})
                                                                             </span>
                                                                         )}
                                                                     </span>
                                                                 )}
                                                             </span>
                                                         ) : (
                                                             <span className="permission-denied">
                                                                 Not Granted
                                                                 {player.permission_notes && (
                                                                     <span className="permission-notes">
                                                                         - {player.permission_notes}
                                                                     </span>
                                                                 )}
                                                             </span>
                                                         )}
                                                     </span>
                                                 </div>
                                             )}
                                            {/* <div className="detail-item">
                                                <span className="label">Rating:</span>
                                                <span className="value">{player.skill_rating}</span>
                                            </div> */}
                                            {filter === 'pending' && (
                                                <div className="detail-item">
                                                    <span className="label">Assigned Shirt:</span>
                                                    <span className="value highlight">{player.shirt_number || nextShirtNumber}</span>
                                                </div>
                                            )}
                                            
                                            {/* Parent information - only show for academy teams */}
                                            {isAcademyTeam() && player.parent_data && (
                                                <div className="parent-info">
                                                                                                    <div className="detail-item">
                                                    <span className="label parent-label">Parent / Guardian:</span>
                                                        <span className="value">
                                                            {(() => {
                                                                try {
                                                                    const parentData = typeof player.parent_data === 'string' 
                                                                        ? JSON.parse(player.parent_data) 
                                                                        : player.parent_data;
                                                                    
                                                                    // Handle both array and single object format
                                                                    const parents = Array.isArray(parentData) ? parentData : [parentData];
                                                                    
                                                                    return parents.map((parent, index) => (
                                                                        <span className="parent-name" key={parent.parent_id || index}>
                                                                            {parent.first_name} {parent.last_name}
                                                                            {parent.relationship && (
                                                                                <span className="relationship-type">
                                                                                    ({parent.relationship})
                                                                                </span>
                                                                            )}
                                                                            {index < parents.length - 1 ? ', ' : ''}
                                                                        </span>
                                                                    ));
                                                                } catch (e) {
                                                                    console.error("Error parsing parent data:", e);
                                                                    return 'No parent linked';
                                                                }
                                                            })()}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            {isAcademyTeam() && !player.parent_data && (
                                                <div className="parent-info">
                                                    <div className="detail-item">
                                                        <span className="label parent-label">Parent:</span>
                                                        <span className="value">No parent linked</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Conditional rendering of buttons based on filter */}
                                {filter === 'pending' && (
                                    // Show accept button for pending players
                                    <button 
                                        className="accept-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAcceptPlayer(player.player_id);
                                        }}
                                    >
                                        Accept Player
                                    </button>
                                )}
                                
                                {(isUserAdmin || authService.isManager()) && (
                                    <div className={`player-dropdown ${openDropdown === player.player_id ? 'open' : ''}`}>
                                        <button 
                                            className="edit-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDropdownToggle(player.player_id);
                                            }}
                                        >
                                            <FaChevronDown className="dropdown-arrow" />
                                        </button>
                                        
                                        {openDropdown === player.player_id && (
                                            <>
                                                <div className="dropdown-backdrop" onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenDropdown(null);
                                                }}></div>
                                                <div className="dropdown-menu">
                                                    <button 
                                                        className="dropdown-item"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownAction('edit', player);
                                                        }}
                                                    >
                                                        Edit Player
                                                    </button>
                                                    {!isAcademyTeam() && (
                                                        <button 
                                                            className="dropdown-item"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDropdownAction('admin', player);
                                                            }}
                                                            disabled={player.is_admin && adminCount <= 1}
                                                        >
                                                            {player.is_admin ? 'Remove Admin' : 'Make Admin'}
                                                        </button>
                                                    )}
                                                    <button 
                                                        className="dropdown-item"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownAction('injured', player);
                                                        }}
                                                    >
                                                        {player.injured_unavailable_at ? 'Mark as Available' : 'Mark as Injured / Unavailable'}
                                                    </button>
                                                    {filter === 'removed' ? (
                                                        <button 
                                                            className="dropdown-item restore-item"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDropdownAction('restore', player);
                                                            }}
                                                        >
                                                            <FaCheckCircle className="restore-icon" />
                                                            Restore Player
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="dropdown-item remove-item"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDropdownAction('remove', player);
                                                            }}
                                                        >
                                                            <FaTrash className="remove-icon" />
                                                            Remove Player
                                                        </button>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                
                                <div className="player-card-actions">
                                    {/* Add Parent / Guardian button - only show for academy teams */}
                                    {isAcademyTeam() && (
                                        <button 
                                            className="add-parent-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenParentInviteModal(player.player_id);
                                            }}
                                        >
                                            Add Parent / Guardian
                                            <FaPlus className="icon" />
                                        </button>
                                    )}
                                    <button 
                                        className="view-profile-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/profile/${player.player_id}`);
                                        }}
                                    >
                                        View Player Profile
                                        <FaChevronRight className="icon" />
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-results">
                            {filter === 'pending'
                                ? 'No players awaiting acceptance.'
                                : filter === 'removed'
                                ? 'No removed players'
                                : 'No players found'
                            }
                        </div>
                    )}
                </div>
            )}

            {showInviteModal && (
                <InvitePlayerModal
                    onClose={() => setShowInviteModal(false)}
                    onSubmit={handleInviteSubmit}
                    inviteCode={inviteCode}
                    onCopyInviteLink={copyToClipboard}
                    playerAdded={playerAdded}
                />
            )}

            {showParentInviteModal && (
                <div className="modal-overlay" onClick={() => setShowParentInviteModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="modal-close"
                            onClick={() => setShowParentInviteModal(false)}
                        >
                            ×
                        </button>
                        <h2>Invite Parent/Guardian</h2>
                        {selectedExistingParent ? (
                            <p>You've selected an existing parent. Please confirm the player to link and permissions:</p>
                        ) : (
                            <p>First, search for existing parents by entering a mobile number or name, then add details if needed:</p>
                        )}
                        
                        <form onSubmit={handleParentFormSubmit} className="parent-invite-form">
                            {/* Player to Link - Always first */}
                            <div className="form-group">
                                <label>Player to Link</label>
                                <select 
                                    name="child_id"
                                    value={parentFormData.child_id}
                                    onChange={handleParentFormChange}
                                    required
                                >
                                    <option value="">Select a player</option>
                                    {acceptedPlayers.map(player => (
                                        <option key={player.player_id} value={player.player_id}>
                                            {player.first_name} {player.last_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Search Section - Combined Search Field */}
                            {!selectedExistingParent && !continueWithNewParent && (
                                <>
                                    <div className="form-group">
                                        <label>Search by Mobile Number or Name</label>
                                        <input 
                                            type="text"
                                            value={parentSearchInput}
                                            onChange={handleParentSearchChange}
                                            placeholder="Enter phone number (e.g. 07123 456789) or name (e.g. John Smith)"
                                        />
                                        <small className="form-note">
                                            <FaInfoCircle className="info-icon" />
                                            Enter a mobile number or at least 2 characters of the parent's name to search for existing parents
                                        </small>
                                    
                                    {/* Existing Parents Search Results */}
                                    {isSearchingParents && (
                                        <div className="existing-parents-loading">
                                            <div className="loading-spinner"></div>
                                            <span>Searching for existing parents...</span>
                                        </div>
                                    )}
                                    
                                    {showExistingParents && existingParents.length > 0 && (
                                        <div className="existing-parents-results">
                                            <div className="existing-parents-header">
                                                <h4>Existing Parents Found</h4>
                                                <p>Click on a parent below to add them to your team or continue to create a new parent</p>
                                            </div>
                                            <div className="existing-parents-list">
                                                {existingParents.map((parent) => (
                                                    <div 
                                                        key={parent.parent_id} 
                                                        className="existing-parent-item"
                                                    >
                                                        {parent.isAlreadyAdded ? (
                                                            // Simplified view for already added parents
                                                            <>
                                                                <div className="parent-basic-info">
                                                                    <div className="parent-name">
                                                                        <strong>{parent.first_name} {parent.last_name}</strong>
                                                                    </div>
                                                                </div>
                                                                <div className="parent-action">
                                                                    <div className="already-added-status">
                                                                        <span className="already-added-badge">Already Added</span>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            // Full view for new parents
                                                            <>
                                                                <div className="parent-basic-info">
                                                                    <div className="parent-name">
                                                                        <strong>{parent.first_name} {parent.last_name}</strong>
                                                                    </div>
                                                                    <div className="parent-contact">
                                                                        <span>Phone: {parent.display_phone === 'Not provided' ? '-' : parent.display_phone}</span>
                                                                        {parent.email && <span>Email: {parent.email}</span>}
                                                                    </div>
                                                                </div>
                                                                <div className="parent-details">
                                                                    <div className="children-info">
                                                                        <span className="info-label">Children:</span>
                                                                        <span className="info-value">
                                                                            {parent.children_names || 'No children linked'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="parent-action">
                                                                    <button 
                                                                        className="choose-parent-btn"
                                                                        onClick={() => handleSelectExistingParent(parent)}
                                                                    >
                                                                        Choose Parent
                                                                    </button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* No Parents Found Message */}
                                    {showExistingParents && existingParents.length === 0 && !isSearchingParents && !continueWithNewParent && (
                                        <div className="form-group">
                                            <div className="no-parents-found-section">
                                                <p className="no-parents-message">
                                                    {parentSearchInput.trim().length >= 2
                                                        ? "No existing parents found with this search. You can create a new parent." 
                                                        : "No existing parents found. You can create a new parent."
                                                    }
                                                </p>
                                                <button 
                                                    type="button"
                                                    className="secondary-btn create-new-parent-btn"
                                                    onClick={() => {
                                                        // Parse search input and populate form fields if it was a name search
                                                        const { phone, firstName, lastName } = parseSearchInput(parentSearchInput);
                                                        if (firstName || lastName) {
                                                            setParentFormData({
                                                                ...parentFormData,
                                                                first_name: firstName || parentFormData.first_name,
                                                                last_name: lastName || parentFormData.last_name,
                                                                phone: phone || parentFormData.phone
                                                            });
                                                        }
                                                        setContinueWithNewParent(true);
                                                    }}
                                                >
                                                    Create New Parent
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    </div>
                                </>
                            )}

                            {/* Selected Parent Info */}
                            {selectedExistingParent && (
                                <div className="selected-parent-info">
                                    <button 
                                        className="remove-parent-btn"
                                        onClick={() => {
                                            setSelectedExistingParent(null);
                                            setParentFormData({
                                                first_name: '',
                                                last_name: '',
                                                phone: '',
                                                email: '',
                                                child_id: parentFormData.child_id,
                                                relationship: '',
                                                canRespondToMatches: true
                                            });
                                        }}
                                    >
                                        ×
                                    </button>
                                    <h4>Selected Parent:<br/><span className="parent-name">{selectedExistingParent.first_name} {selectedExistingParent.last_name}</span></h4>
                                    <p>Phone: {selectedExistingParent.display_phone === 'Not provided' ? '-' : formatPhoneForDisplay(selectedExistingParent.display_phone)}</p>
                                    {selectedExistingParent.email && <p>Email: {selectedExistingParent.email}</p>}
                                    <p>Children: {selectedExistingParent.children_names || 'No children linked'}</p>
                                </div>
                            )}

                            {/* New Parent Fields - Only show when explicitly continuing with new parent */}
                            {continueWithNewParent && !selectedExistingParent && (
                                <>
                                    <div className="form-group">
                                        <label>First Name</label>
                                        <input 
                                            type="text"
                                            name="first_name"
                                            value={parentFormData.first_name}
                                            onChange={handleParentFormChange}
                                            placeholder="Parent's first name"
                                            required
                                        />
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Last Name</label>
                                        <input 
                                            type="text"
                                            name="last_name"
                                            value={parentFormData.last_name}
                                            onChange={handleParentFormChange}
                                            placeholder="Parent's last name"
                                            required
                                        />
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Mobile Number</label>
                                        <input 
                                            type="tel"
                                            name="phone"
                                            value={parentFormData.phone}
                                            onChange={handleParentFormChange}
                                            placeholder="07xxx xxxxx"
                                            required
                                        />
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Email (Optional)</label>
                                        <input 
                                            type="email"
                                            name="email"
                                            value={parentFormData.email}
                                            onChange={handleParentFormChange}
                                            placeholder="parent@example.com"
                                        />
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Relationship (Optional)</label>
                                        <select
                                            name="relationship"
                                            value={parentFormData.relationship || ''}
                                            onChange={handleParentFormChange}
                                        >
                                            <option value="">Select relationship</option>
                                            <option value="Mother">Mother</option>
                                            <option value="Father">Father</option>
                                            <option value="Step-mother">Step-mother</option>
                                            <option value="Step-father">Step-father</option>
                                            <option value="Guardian">Guardian</option>
                                            <option value="Grandmother">Grandmother</option>
                                            <option value="Grandfather">Grandfather</option>
                                            <option value="Aunt">Aunt</option>
                                            <option value="Uncle">Uncle</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* Continue with New Parent button - show when existing parents found but none selected and not all are already added */}
                            {showExistingParents && existingParents.length > 0 && !selectedExistingParent && !continueWithNewParent && existingParents.some(parent => !parent.isAlreadyAdded) && (
                                <div className="form-group">
                                    <div className="continue-new-parent-section">
                                        <p className="section-divider">— or —</p>
                                        <button 
                                            type="button"
                                            className="secondary-btn continue-new-parent-btn"
                                            onClick={() => {
                                                // Parse search input and populate form fields if it was a name search
                                                const { phone, firstName, lastName } = parseSearchInput(parentSearchInput);
                                                if (firstName || lastName) {
                                                    setParentFormData({
                                                        ...parentFormData,
                                                        first_name: firstName || parentFormData.first_name,
                                                        last_name: lastName || parentFormData.last_name,
                                                        phone: phone || parentFormData.phone
                                                    });
                                                }
                                                setContinueWithNewParent(true);
                                            }}
                                        >
                                            Create New Parent Instead
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Permissions section - only show when there are results or creating new parent, but hide if there's only 1 result and that person is already added */}
                            {(selectedExistingParent || continueWithNewParent || (showExistingParents && existingParents.length > 0 && !(existingParents.length === 1 && existingParents[0].isAlreadyAdded))) && (
                                <>
                                    <hr className="permissions-divider" />
                                    <div className="form-group permissions-section">
                                        <h4>Permissions</h4>
                                        
                                        <div className="permission-item">
                                            <div className="ios-toggle-wrapper">
                                                <input
                                                    type="checkbox"
                                                    id="canRespondToMatches"
                                                    name="canRespondToMatches"
                                                    checked={parentFormData.canRespondToMatches}
                                                    onChange={handleParentFormChange}
                                                    className="ios-toggle"
                                                />
                                                <label htmlFor="canRespondToMatches" className="ios-toggle-label"></label>
                                            </div>
                                            <label htmlFor="canRespondToMatches" className="permission-label">
                                                Allow this person to respond to match/training requests
                                            </label>
                                        </div>
                                    </div>
                                </>
                            )}
                            

                            
                            <div className="button-group">
                                <button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={isSubmittingParentForm || !isParentFormValid()}
                                >
                                    {isSubmittingParentForm ? 'Processing...' : 
                                     !isParentFormValid() ? 'Please fill in required fields' :
                                     selectedExistingParent ? 'Add Parent to Team' : 'Invite Parent/Guardian'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reject Parent Modal */}
            {showRejectModal && (
                <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Reject Parent Registration</h2>
                        <p>Please provide a reason for rejecting this parent registration:</p>
                        
                        <div className="form-group">
                            <label>Rejection Reason</label>
                            <textarea
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Enter reason for rejection..."
                                rows="4"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    background: '#2c2c2c',
                                    border: '1px solid #555',
                                    color: 'white',
                                    borderRadius: '5px',
                                    resize: 'vertical'
                                }}
                            />
                        </div>
                        
                        <div className="button-group" style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setShowRejectModal(false)}
                                style={{
                                    background: '#2c2c2c',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    padding: '10px 20px',
                                    cursor: 'pointer',
                                    fontFamily: 'Audiowide, sans-serif'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="reject-btn"
                                onClick={handleRejectSubmit}
                                style={{
                                    background: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    padding: '10px 20px',
                                    cursor: 'pointer',
                                    fontFamily: 'Audiowide, sans-serif'
                                }}
                            >
                                Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Player Modal - Styled like StageTwo.js */}
            {showAddPlayerModal && (
                <div className="modal-overlay" onClick={() => setShowAddPlayerModal(false)}>
                    <div className="modal add-player-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Add New Player</h2>
                        
                        <p className="modal-intro">
                            Firstly, add a new player below. This will add their details to the app where you can then invite a parent/guardian to link their account.
                        </p>
                        
                        <div className="stage-indicator">
                            {Array.from({ length: totalStages }, (_, i) => (
                                <div 
                                    key={i} 
                                    className={`stage-dot ${currentStage >= i + 1 ? 'active' : ''}`}
                                    onClick={() => {
                                        // Allow going back to previous stages, but not forward without validation
                                        if (i + 1 < currentStage) {
                                            setCurrentStage(i + 1);
                                        }
                                    }}
                                />
                            ))}
                        </div>
                        
                        <h3 className="stage-title">
                            {currentStage === 1 && "Player Details"}
                            {currentStage === 2 && "Position & Shirt Number"}
                            {currentStage === 3 && "Player Abilities"}
                        </h3>
                        
                        <form onSubmit={handleAddPlayerSubmit} className="add-player-form">
                            {/* Stage 1: Player Details */}
                            {currentStage === 1 && (
                                <div className="form-section player-details">
                                    <div className="form-group">
                                        <label>First Name</label>
                                        <input 
                                            type="text"
                                            name="first_name"
                                            value={newPlayerFormData.first_name}
                                            onChange={handleNewPlayerFormChange}
                                            placeholder="Enter first name"
                                            className={fieldErrors.first_name ? 'error' : ''}
                                            required
                                        />
                                        {fieldErrors.first_name && <div className="field-error">{fieldErrors.first_name}</div>}
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Last Name</label>
                                        <input 
                                            type="text"
                                            name="last_name"
                                            value={newPlayerFormData.last_name}
                                            onChange={handleNewPlayerFormChange}
                                            placeholder="Enter last name"
                                            className={fieldErrors.last_name ? 'error' : ''}
                                            required
                                        />
                                        {fieldErrors.last_name && <div className="field-error">{fieldErrors.last_name}</div>}
                                        
                                        {/* Existing Players Search Results */}
                                        {isSearchingPlayers && (
                                            <div className="existing-players-loading">
                                                <div className="loading-spinner"></div>
                                                <span>Searching for existing players...</span>
                                            </div>
                                        )}
                                        
                                        {showExistingPlayers && existingPlayers.length > 0 && (
                                            <div className="existing-players-results">
                                                <div className="existing-players-header">
                                                    <h4>Existing Players Found</h4>
                                                    <p>Click on a player below to add them to your team or continue to create a new player</p>
                                                </div>
                                                <div className="existing-players-list">
                                                    {existingPlayers.map((player) => (
                                                        <div 
                                                            key={player.player_id} 
                                                            className="existing-player-item"
                                                        >
                                                            <div className="player-basic-info">
                                                                <div className="player-name">
                                                                    <strong>{player.first_name} {player.last_name}</strong>
                                                                </div>
                                                                <div className="player-contact">
                                                                    <span>Phone: {player.display_phone === 'Not provided' ? '-' : formatPhoneForDisplay(player.display_phone)}</span>
                                                                    {player.email && <span>Email: {player.email}</span>}
                                                                </div>
                                                            </div>
                                                                                                                            <div className="player-details">
                                                                    <div className="teams-info">
                                                                        <span className="info-label">Teams:</span>
                                                                        <span className="info-value">
                                                                            {player.teams.length > 0 
                                                                                ? player.teams.map(team => `${team.team_name} (${team.age_group})`).join(', ')
                                                                                : 'No teams'
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    <div className="parents-info">
                                                                        <span className="info-label">Parents / Guardian:</span>
                                                                        <span className="info-value">
                                                                            {player.parents.length > 0 
                                                                                ? player.parents.map((parent, index) => (
                                                                                    <span key={parent.parent_id}>
                                                                                        {parent.first_name} {parent.last_name} ({parent.relationship})
                                                                                        {index < player.parents.length - 1 && ', '}
                                                                                    </span>
                                                                                  ))
                                                                                : 'No parents linked'
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="player-action">
                                                                    <button 
                                                                        className="choose-player-btn"
                                                                        onClick={() => handleSelectExistingPlayer(player)}
                                                                    >
                                                                        Choose Player
                                                                    </button>
                                                                </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {!isAcademyU13OrYounger() && (
                                        <>
                                            <div className="form-group">
                                                <label>Mobile Number{isAcademyTeam() ? ' (Optional)' : ''}</label>
                                                <input 
                                                    type="tel"
                                                    name="phone"
                                                    value={newPlayerFormData.phone}
                                                    onChange={handleNewPlayerFormChange}
                                                    placeholder="+44 7123 456789 or 07123 456789"
                                                    maxLength="20"
                                                    className={fieldErrors.phone ? 'error' : ''}
                                                    required={!isAcademyTeam()}
                                                />
                                                {fieldErrors.phone && <div className="field-error">{fieldErrors.phone}</div>}
                                            </div>
                                            
                                            <div className="form-group">
                                                <label>Email (Optional)</label>
                                                <input 
                                                    type="email"
                                                    name="email"
                                                    value={newPlayerFormData.email}
                                                    onChange={handleNewPlayerFormChange}
                                                    placeholder="player@example.com"
                                                    className={fieldErrors.email ? 'error' : ''}
                                                />
                                                {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
                                            </div>
                                        </>
                                    )}
                                    
                                    {isAcademyU13OrYounger() && (
                                        <div className="form-notice">
                                            <p>
                                                <FaInfoCircle className="info-icon" />
                                                Contact details are not required for academy players aged 13 or younger. These can be added later by a parent or guardian.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Stage 2: Position & Shirt Number or Success Message */}
                            {currentStage === 2 && (
                                <div className="form-section position-stage">
                                    {playerAdded && playerAdded.isExistingPlayer ? (
                                        // Show success message for existing player
                                        <div className="success-message player-added-success">
                                            <div className="success-icon">
                                                <FaCheckCircle size={32} />
                                            </div>
                                            <div className="success-content">
                                                <h3>Player Added Successfully!</h3>
                                                <p>
                                                    <strong>{playerAdded.name}</strong> has been added to your team. 
                                                    They are now available for selection in matches and training sessions.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        // Show position and shirt number form for new players
                                        <>
                                    <div className="form-group">
                                        <div className="foot-preference">
                                            <label>Preferred Foot</label>
                                            <div className="foot-options">
                                                {footOptions.map(option => (
                                                    <div 
                                                        key={option}
                                                        className={`foot-option ${newPlayerFormData.preferredFoot === option ? 'selected' : ''}`}
                                                        onClick={() => handleFootPreference(option)}
                                                    >
                                                        {option}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        <label>Position</label>
                                        <select 
                                            name="position" 
                                            value={newPlayerFormData.position} 
                                            onChange={handleNewPlayerFormChange}
                                            className="position-select"
                                            required
                                        >
                                            {positions.map(position => (
                                                <option key={position.value} value={position.value}>
                                                    {position.label}
                                                </option>
                                            ))}
                                        </select>
                                        
                                        <label>Shirt Number</label>
                                        <select
                                            name="shirtNumber"
                                            value={newPlayerFormData.shirtNumber}
                                            onChange={handleNewPlayerFormChange}
                                            className={`shirt-number-select ${fieldErrors.shirtNumber ? 'error' : ''}`}
                                            required
                                        >
                                            <option value="">Select Shirt Number</option>
                                            {availableShirtNumbers.map(number => (
                                                <option key={number} value={number.toString()}>
                                                    {number}
                                                </option>
                                            ))}
                                        </select>
                                        {fieldErrors.shirtNumber && <div className="field-error">{fieldErrors.shirtNumber}</div>}
                                        {shirtNumberError && !fieldErrors.shirtNumber && <div className="error-message">{shirtNumberError}</div>}
                                    </div>
                                        </>
                                    )}
                                </div>
                            )}
                            
                            {/* Stage 3: Player Abilities */}
                            {currentStage === 3 && (
                                <div className="player-ratings-container">
                                    <p className="rating-instructions">Adjust the sliders to rate the player's abilities</p>
                                    <p className="abilities-note">These ratings are used to create balanced formations when generating teams. They can be updated at any time. This information will only be displayed for age groups 13 and older.</p>
                                    
                                    <div className="player-ratings">
                                        <div className="ratings-hexagon">
                                            <svg viewBox="0 0 100 100" className="ratings-chart">
                                                {/* Background grid */}
                                                <g className="grid">
                                                    {[20, 40, 60, 80, 100].map((percent, i) => (
                                                        <polygon
                                                            key={i}
                                                            points={[0, 1, 2, 3, 4, 5].map(j => {
                                                                const p = calculatePoint(percent, j);
                                                                return `${p.x},${p.y}`;
                                                            }).join(' ')}
                                                            className="grid-line"
                                                        />
                                                    ))}
                                                </g>
                                                
                                                {/* Stats polygon */}
                                                <polygon 
                                                    points={[
                                                        calculatePoint(newPlayerFormData.pace, 0),
                                                        calculatePoint(newPlayerFormData.shooting, 1),
                                                        calculatePoint(newPlayerFormData.passing, 2),
                                                        calculatePoint(newPlayerFormData.physical, 3),
                                                        calculatePoint(newPlayerFormData.defending, 4),
                                                        calculatePoint(newPlayerFormData.dribbling, 5),
                                                    ].map(p => `${p.x},${p.y}`).join(' ')} 
                                                    className={`stats-polygon ${isAnimating ? 'animate' : ''}`} 
                                                />
                                                
                                                {/* Stat points */}
                                                {[
                                                    calculatePoint(newPlayerFormData.pace, 0),
                                                    calculatePoint(newPlayerFormData.shooting, 1),
                                                    calculatePoint(newPlayerFormData.passing, 2),
                                                    calculatePoint(newPlayerFormData.physical, 3),
                                                    calculatePoint(newPlayerFormData.defending, 4),
                                                    calculatePoint(newPlayerFormData.dribbling, 5),
                                                ].map((point, i) => (
                                                    <circle
                                                        key={i}
                                                        cx={point.x}
                                                        cy={point.y}
                                                        r="2"
                                                        className={`stat-point ${isAnimating ? 'animate' : ''}`}
                                                    />
                                                ))}
                                            </svg>
                                            
                                            {/* Labels */}
                                            <div className="stat-label pace">PAC</div>
                                            <div className="stat-label shooting">SHO</div>
                                            <div className="stat-label passing">PAS</div>
                                            <div className="stat-label physical">PHY</div>
                                            <div className="stat-label defending">DEF</div>
                                            <div className="stat-label dribbling">DRI</div>
                                        </div>
                                    </div>
                                    
                                    <div className="rating-sliders">
                                        <div className="rating-slider">
                                            <label>
                                                <span className="slider-label">Pace</span>
                                                <span className="slider-value">{newPlayerFormData.displayPace}<span className="scale-indicator">/10</span></span>
                                            </label>
                                            <input 
                                                id="pace-slider-add"
                                                type="range" 
                                                min="0" 
                                                max="10" 
                                                value={newPlayerFormData.displayPace} 
                                                onChange={(e) => handleRatingChange('pace', e.target.value)} 
                                            />
                                        </div>
                                        
                                        <div className="rating-slider">
                                            <label>
                                                <span className="slider-label">Shooting</span>
                                                <span className="slider-value">{newPlayerFormData.displayShooting}<span className="scale-indicator">/10</span></span>
                                            </label>
                                            <input 
                                                id="shooting-slider-add"
                                                type="range" 
                                                min="0" 
                                                max="10" 
                                                value={newPlayerFormData.displayShooting} 
                                                onChange={(e) => handleRatingChange('shooting', e.target.value)} 
                                            />
                                        </div>
                                        
                                        <div className="rating-slider">
                                            <label>
                                                <span className="slider-label">Passing</span>
                                                <span className="slider-value">{newPlayerFormData.displayPassing}<span className="scale-indicator">/10</span></span>
                                            </label>
                                            <input 
                                                id="passing-slider-add"
                                                type="range" 
                                                min="0" 
                                                max="10" 
                                                value={newPlayerFormData.displayPassing} 
                                                onChange={(e) => handleRatingChange('passing', e.target.value)} 
                                            />
                                        </div>
                                        
                                        <div className="rating-slider">
                                            <label>
                                                <span className="slider-label">Dribbling</span>
                                                <span className="slider-value">{newPlayerFormData.displayDribbling}<span className="scale-indicator">/10</span></span>
                                            </label>
                                            <input 
                                                id="dribbling-slider-add"
                                                type="range" 
                                                min="0" 
                                                max="10" 
                                                value={newPlayerFormData.displayDribbling} 
                                                onChange={(e) => handleRatingChange('dribbling', e.target.value)} 
                                            />
                                        </div>
                                        
                                        <div className="rating-slider">
                                            <label>
                                                <span className="slider-label">Defending</span>
                                                <span className="slider-value">{newPlayerFormData.displayDefending}<span className="scale-indicator">/10</span></span>
                                            </label>
                                            <input 
                                                id="defending-slider-add"
                                                type="range" 
                                                min="0" 
                                                max="10" 
                                                value={newPlayerFormData.displayDefending} 
                                                onChange={(e) => handleRatingChange('defending', e.target.value)} 
                                            />
                                        </div>
                                        
                                        <div className="rating-slider">
                                            <label>
                                                <span className="slider-label">Physical</span>
                                                <span className="slider-value">{newPlayerFormData.displayPhysical}<span className="scale-indicator">/10</span></span>
                                            </label>
                                            <input 
                                                id="physical-slider-add"
                                                type="range" 
                                                min="0" 
                                                max="10" 
                                                value={newPlayerFormData.displayPhysical} 
                                                onChange={(e) => handleRatingChange('physical', e.target.value)} 
                                            />
                                        </div>
                                        
                                        {ratingsError && <div className="error-message ratings-error">{ratingsError}</div>}
                                    </div>
                                </div>
                            )}
                            
                            <div className="stage-navigation">
                                {currentStage > 1 && !playerAdded?.isExistingPlayer && (
                                    <button
                                        type="button"
                                        className="previous-btn"
                                        onClick={handlePreviousStage}
                                    >
                                        Previous
                                    </button>
                                )}
                                
                                {currentStage === 2 && playerAdded?.isExistingPlayer ? (
                                    <button
                                        type="button"
                                        className="submit-btn"
                                        onClick={() => setShowAddPlayerModal(false)}
                                    >
                                        Done
                                    </button>
                                ) : currentStage < totalStages ? (
                                    <button
                                        type="button"
                                        className="next-btn"
                                        onClick={handleNextStage}
                                    >
                                        Next
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        className="submit-btn"
                                        disabled={isSubmittingPlayerForm}
                                    >
                                        {isSubmittingPlayerForm ? 'Adding Player...' : 'Add Player'}
                                    </button>
                                )}
                            </div>
                            
                            <button
                                type="button"
                                className="close-btn"
                                onClick={() => setShowAddPlayerModal(false)}
                            >
                                ✕
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {showEditModal && selectedPlayer && (
                <EditPlayerModal 
                    player={selectedPlayer}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedPlayer(null);
                    }}
                    onSubmit={handleEditSubmit}
                />
            )}
        </div>
    );
}

// Add CSS for the pending count badge
const styles = `
    .pending-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background-color: #ff4757;
        color: white;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        font-size: 12px;
        margin-left: 5px;
        font-weight: bold;
    }

    /* Player details layout */
    .player-basic-info {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .player-details {
        margin-bottom: 0;
    }

    .detail-item .highlight {
        color: #ffd700;
        font-weight: bold;
    }

    /* Dark mode player stats styling */
    .stats-container {
        margin-top: 5px;
        border-top: 1px solid #333;
        padding-top: 15px;
        width: 100%;
    }

    .player-stats {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 12px;
        border-radius: 8px;
        width: 100%;
    }

    .player-stats .stat {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 8px;
        border-radius: 6px;
        background-color: #2c2c2c;
        transition: transform 0.2s, box-shadow 0.2s;
    }

    .player-stats .stat:hover {
        transform: translateY(-2px);
        box-shadow: 0 3px 6px rgba(0,0,0,0.3);
    }

    .player-stats .stat .label {
        font-size: 10px;
        font-weight: bold;
        color: #a7a7a7;
        text-transform: uppercase;
        margin-bottom: 2px;
    }

    .player-stats .stat .value {
        font-size: 16px;
        font-weight: bold;
        color: #ffd700;
    }

    .player-stats .stat-bar {
        width: 100%;
        height: 4px;
        background-color: #444;
        border-radius: 2px;
        margin-top: 4px;
        overflow: hidden;
    }

    .player-stats .stat-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.3s ease;
    }

    /* Adjust player card layout */
    .player-card {
        display: flex;
        flex-direction: column;
        position: relative;
        padding-bottom: 60px; /* Add space for the button at bottom */
    }

    .player-card .player-info {
        margin-bottom: 0;
    }

    .player-card .admin-toggle-btn,
    .player-card .accept-btn {
        margin-top: 15px;
    }

    /* Accept button styling */
    .accept-btn {
        width: 100%;
        padding: 10px;
        border: none;
        border-radius: 5px;
        background-color: #4cd137;
        color: #fff;
        font-family: "Audiowide", sans-serif;
        cursor: pointer;
        transition: all 0.3s ease;
    }

    .accept-btn:hover {
        background-color: #44bd32;
        transform: translateY(-2px);
    }
    
    /* SMS Invite Styles */
    .divider {
        display: flex;
        align-items: center;
        text-align: center;
        margin: 20px 0;
        color: #777;
    }
    
    .divider::before,
    .divider::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid #444;
    }
    
    .divider span {
        margin: 0 10px;
        font-size: 14px;
    }
    
    .sms-invite {
        margin-top: 15px;
    }
    
    .sms-invite .form-group {
        margin-bottom: 15px;
    }
    
    .sms-invite label {
        display: block;
        margin-bottom: 5px;
        color: #ddd;
    }
    
    .sms-invite input {
        width: 100%;
        padding: 10px;
        border: 1px solid #555;
        background-color: #333;
        color: white;
        border-radius: 4px;
    }
    
    .sms-invite .error-message {
        color: #ff4757;
        font-size: 14px;
        margin-top: 5px;
    }
    
    .sms-invite .success-message {
        color: #4cd137;
        font-size: 14px;
        margin-top: 5px;
        padding: 8px;
        background-color: rgba(76, 209, 55, 0.1);
        border-radius: 4px;
        text-align: center;
    }
    
    .sms-invite button {
        width: 100%;
        padding: 10px;
        border: none;
        background-color: #2196F3;
        color: white;
        border-radius: 4px;
        cursor: pointer;
        font-weight: bold;
        transition: background-color 0.3s;
    }
    
    .sms-invite button:hover {
        background-color: #1976D2;
    }
    
    .sms-invite button:disabled {
        background-color: #555;
        cursor: not-allowed;
    }
    
    .sms-invite button.loading {
        position: relative;
        color: transparent;
    }
    
    .sms-invite button.loading::after {
        content: "";
        position: absolute;
        width: 20px;
        height: 20px;
        top: 50%;
        left: 50%;
        margin: -10px 0 0 -10px;
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        border-top-color: white;
        animation: spin 1s ease-in-out infinite;
    }
    
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
    
    .sms-invite .info-text {
        color: #777;
        font-size: 12px;
        margin-top: 10px;
        text-align: center;
    }
    
    /* Profile completion note styling */
    .profile-completion-note {
        color: #FFD700;
        font-size: 14px;
        margin-top: 15px;
        padding: 10px;
        background-color: rgba(255, 215, 0, 0.1);
        border-radius: 4px;
        text-align: center;
        border-left: 3px solid #FFD700;
    }
    
    /* Parent Invite Modal Styles */
    .parent-invite-form {
        margin-top: 15px;
    }
    
    .parent-invite-form .form-group {
        margin-bottom: 15px;
    }
    
    .parent-invite-form label {
        display: block;
        margin-bottom: 5px;
        color: #ddd;
        font-weight: bold;
    }
    
    .parent-invite-form input,
    .parent-invite-form select {
        width: 100%;
        padding: 10px;
        border: 1px solid #555;
        background-color: #333;
        color: white;
        border-radius: 4px;
    }
    
    .parent-invite-form select {
        cursor: pointer;
    }
    
    .parent-invite-form .button-group {
        display: flex;
        justify-content: space-between;
        margin-top: 20px;
    }
    
    .parent-invite-form .cancel-btn,
    .parent-invite-form .submit-btn {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        font-weight: bold;
        cursor: pointer;
        transition: background-color 0.3s;
    }
    
    .parent-invite-form .cancel-btn {
        background-color: #555;
        color: white;
    }
    
    .parent-invite-form .cancel-btn:hover {
        background-color: #444;
    }
    
    .parent-invite-form .submit-btn {
        background-color: #2196F3;
        color: white;
    }
    
    .parent-invite-form .submit-btn:hover {
        background-color: #1976D2;
    }
    
    .invite-parent-btn {
        margin-left: 10px;
        background-color: #FF9800;
    }
    
    .invite-parent-btn:hover {
        background-color: #F57C00;
    }
    
    /* Parent info styles */
    .parent-info {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px dashed #444;
    }
    
    .parent-label {
        color: #FF9800;
        font-weight: bold;
    }

    /* Add Player button and modal styles */
    .add-player-btn {
        background-color: #4CAF50;  /* Green color for add player button */
        margin-left: 10px;
    }
    
    .add-player-btn:hover {
        background-color: #388E3C;
    }
    
    .add-player-modal {
        max-width: 700px;
        max-height: 80vh;
        overflow-y: auto;
    }
    
    .add-player-form .form-section h3 {
        margin-top: 0;
        color: #FFD700;
        font-size: 1.1rem;
        margin-bottom: 15px;
    }
    
    /* Player Rating Hexagon Styles from StageTwo.js */
    .player-ratings-container {
        margin-top: 20px;
        padding: 15px;
        background-color: #222;
        border-radius: 8px;
    }
    
    .player-ratings-container h4 {
        margin-top: 0;
        color: #FFD700;
        font-size: 1.1rem;
        text-align: center;
    }
    
    .rating-instructions {
        color: #999;
        text-align: center;
        font-size: 0.9rem;
        margin-bottom: 20px;
    }
    
    .player-ratings {
        display: flex;
        justify-content: center;
        margin-bottom: 20px;
    }
    
    .ratings-hexagon {
        position: relative;
        width: 200px;
        height: 200px;
        margin: 0 auto;
    }
    
    .ratings-chart {
        width: 100%;
        height: 100%;
    }
    
    .grid-line {
        fill: none;
        stroke: rgba(255, 255, 255, 0.1);
        stroke-width: 0.5;
    }
    
    .stats-polygon {
        fill: rgba(255, 215, 0, 0.2);
        stroke: #FFD700;
        stroke-width: 2;
        transition: all 0.5s ease-out;
        opacity: 0;
    }
    
    .stats-polygon.animate {
        opacity: 1;
    }
    
    .stat-point {
        fill: #FFD700;
        opacity: 0;
        transition: all 0.5s ease-out;
    }
    
    .stat-point.animate {
        opacity: 1;
    }
    
    .stat-label {
        position: absolute;
        font-size: 12px;
        font-weight: bold;
        color: #FFF;
    }
    
    .stat-label.pace {
        top: 0;
        left: 50%;
        transform: translateX(-50%);
    }
    
    .stat-label.shooting {
        top: 25%;
        right: 0;
        transform: translateY(-50%);
    }
    
    .stat-label.passing {
        bottom: 25%;
        right: 0;
        transform: translateY(50%);
    }
    
    .stat-label.physical {
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
    }
    
    .stat-label.defending {
        bottom: 25%;
        left: 0;
        transform: translateY(50%);
    }
    
    .stat-label.dribbling {
        top: 25%;
        left: 0;
        transform: translateY(-50%);
    }
    
    .rating-sliders {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 15px;
    }
    
    @media (max-width: 600px) {
        .rating-sliders {
            grid-template-columns: 1fr;
        }
    }
    
    .rating-slider {
        margin-bottom: 10px;
    }
    
    .rating-slider label {
        display: flex;
        justify-content: space-between;
        margin-bottom: 5px;
    }
    
    .slider-label {
        font-weight: bold;
        color: #ddd;
    }
    
    .slider-value {
        color: #FFD700;
        font-weight: bold;
    }
    
    .scale-indicator {
        color: #999;
        font-size: 0.8em;
        margin-left: 2px;
    }
    
    .rating-slider input[type="range"] {
        width: 100%;
        height: 6px;
        -webkit-appearance: none;
        background: linear-gradient(to right, #FFD700 var(--value-percent, 50%), #444 var(--value-percent, 50%));
        border-radius: 3px;
        outline: none;
    }
    
    .rating-slider input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #FFD700;
        cursor: pointer;
        border: 2px solid #222;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    }
    
    .rating-slider input[type="range"]::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #FFD700;
        cursor: pointer;
        border: 2px solid #222;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    }
    
    .error-message.ratings-error {
        grid-column: 1 / -1;
        color: #ff4757;
        text-align: center;
        padding: 5px;
        background-color: rgba(255, 71, 87, 0.1);
        border-radius: 4px;
        margin-top: 10px;
    }
    
    /* Foot preference styles */
    .foot-preference {
        margin-bottom: 15px;
    }
    
    .foot-options {
        display: flex;
        gap: 10px;
        margin-top: 5px;
    }
    
    .foot-option {
        flex: 1;
        padding: 8px 12px;
        text-align: center;
        background-color: #333;
        border: 1px solid #555;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .foot-option:hover {
        background-color: #444;
    }
    
    .foot-option.selected {
        background-color: #1976D2;
        border-color: #1976D2;
        color: white;
        font-weight: bold;
    }
    
    .position-select,
    .shirt-number-select {
        width: 100%;
        padding: 10px;
        margin-bottom: 15px;
        background-color: #333;
        border: 1px solid #555;
        color: white;
        border-radius: 4px;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='%23FFFFFF' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px center;
    }


`;

// Add the styles to the document
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
}

export default Players; 